import React, { forwardRef, useImperativeHandle } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { DateRageType, DATE_FORMAT } from '../../../types/common/date';

export interface DateRangePickerProps {
  value?: DateRageType;
  format: DATE_FORMAT;
  showTime: boolean;
  min?: string | null;
  max?: string | null;
  onChange?: (value: DateRageType) => void;
}

const DateRangePicker = forwardRef((props: DateRangePickerProps, ref) => {
  const { value, format, min, max, onChange, showTime } = props;

  const startDateDayjs = value?.startDate ? dayjs(new Date(value?.startDate)) : null;
  const endDateDayjs = value?.endDate ? dayjs(new Date(value?.endDate)) : null;

  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  const disabledDate = (date: Dayjs): boolean => {
    if (min && max) {
      const minDayjs = dayjs(min);
      const maxDayjs = dayjs(max);
      return date.isBefore(minDayjs, 'day') || date.isAfter(maxDayjs, 'day');
    }
    if (min) {
      return date.isBefore(dayjs(min), 'day');
    }
    if (max) {
      return date.isAfter(dayjs(max), 'day');
    }

    return false;
  };

  const disabledStartDate = (startValue: Dayjs): boolean => {
    if (!startValue || !endDateDayjs) {
      return false;
    }
    return disabledDate(startValue) || startValue.isAfter(endDateDayjs);
  };

  const disabledEndDate = (endValue: Dayjs): boolean => {
    if (!endValue || !startDateDayjs) {
      return false;
    }

    return disabledDate(endValue) || endValue.isBefore(startDateDayjs);
  };

  const handleStartChange = (value: Dayjs | null): void => {
    if (value && onChange)
      onChange({ startDate: value.format(format), endDate: endDateDayjs && endDateDayjs.format(format) });
  };

  const handleEndChange = (value: Dayjs | null): void => {
    if (value && onChange)
      onChange({ startDate: startDateDayjs && startDateDayjs.format(format), endDate: value.format(format) });
  };

  return (
    <div>
      <DatePicker
        disabledDate={disabledStartDate}
        showTime={showTime}
        format={format}
        value={startDateDayjs}
        placeholder="시작일"
        onChange={handleStartChange}
        allowClear={false}
      />
      <span> - </span>
      <DatePicker
        disabledDate={disabledEndDate}
        showTime={showTime}
        format={format}
        value={endDateDayjs}
        placeholder="종료일"
        onChange={handleEndChange}
        allowClear={false}
      />
    </div>
  );
});
DateRangePicker.displayName = 'DateRangePicker';

export default DateRangePicker;
